import { useEffect, useRef, useState } from 'react';
import SafeIcon from './SafeIcon';
import MaterialPicker, { type PickedItem } from './MaterialPicker';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { postStatusHoldsDailySlot, useDemo, useWorkspaceData } from '../store/DemoStore';
import { dayFrom } from '../store/campaigns';
import { DEMO_TODAY } from '../store/seed';
import type { Channel } from '../types';
import { channelIconName } from './campaignDisplay';

const DEMO_YEAR = 2026;
const NOVEMBER_MONTH_INDEX = 10;
const MILLISECONDS_PER_DAY = 86400000;
const DAYS_EARLIER_THE_POST_MAY_MOVE = 3;
const DAYS_LATER_TO_SEARCH_FOR_A_FREE_SLOT = 21;
const MINIMUM_IDEA_LENGTH = 5;

const isoDateFromDemoDay = (demoDay: number) => {
  const date = new Date(DEMO_YEAR, NOVEMBER_MONTH_INDEX, demoDay);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const demoDayFromIsoDate = (isoDate: string) => {
  const [year, month, dayOfMonth] = isoDate.split('-').map(Number);
  return Math.round(
    (new Date(year, month - 1, dayOfMonth).getTime() -
      new Date(DEMO_YEAR, NOVEMBER_MONTH_INDEX, 0).getTime()) /
      MILLISECONDS_PER_DAY
  );
};

const isBusinessAccount = (account: { type: string }) => !/personal/i.test(account.type);

export default function AddIdeaSheet({
  onClose,
  defaultCampaign,
}: {
  onClose: () => void;
  defaultCampaign?: string;
}) {
  const { translate, readOnly, language } = useApp();
  const { route, actor, notify, navigate } = useUi();
  const { commands } = useDemo();
  const { accounts, campaigns, posts } = useWorkspaceData(route.workspaceId);
  const networks = [
    ...new Set(accounts.filter(isBusinessAccount).map((account) => account.network)),
  ] as Channel[];
  const [text, setText] = useState('');
  const [materials, setMaterials] = useState<PickedItem[]>([]);
  const [picking, setPicking] = useState(false);
  const [day, setDay] = useState<number | undefined>(undefined);
  const [dayPickedByClient, setDayPickedByClient] = useState(false);
  const [campaignId, setCampaignId] = useState(defaultCampaign ?? '');
  const [allowAfter, setAllowAfter] = useState(false);
  const [channels, setChannels] = useState<Channel[]>(
    ['Instagram', 'Facebook'].filter((channel) =>
      networks.includes(channel as Channel)
    ) as Channel[]
  );
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    fieldRef.current?.focus();
  }, []);
  useEffect(
    function suggestDayFromTextUntilClientPicksOne() {
      if (!dayPickedByClient) setDay(dayFrom(text));
    },
    [text, dayPickedByClient]
  );
  const activeCampaigns = campaigns.filter((campaign) => campaign.status !== 'Finished');
  const isSlotFree = (channel: Channel, candidateDay: number) =>
    candidateDay > DEMO_TODAY &&
    !posts.some(
      (post) =>
        post.channel === channel &&
        post.day === candidateDay &&
        postStatusHoldsDailySlot(post.status)
    );
  const fullChannels = day ? channels.filter((channel) => !isSlotFree(channel, day)) : [];

  function proposeSlotForChannel(channel: Channel, wantedDay: number) {
    const daysOnOrBeforeWantedDay = Array.from(
      { length: DAYS_EARLIER_THE_POST_MAY_MOVE + 1 },
      (_, daysEarlier) => wantedDay - daysEarlier
    );
    const freeDayOnOrBefore = daysOnOrBeforeWantedDay.find((candidateDay) =>
      isSlotFree(channel, candidateDay)
    );
    let freeDayAfterNeedingConsent: number | undefined;
    if (!freeDayOnOrBefore)
      for (
        let candidateDay = wantedDay + 1;
        candidateDay <= wantedDay + DAYS_LATER_TO_SEARCH_FOR_A_FREE_SLOT;
        candidateDay++
      )
        if (isSlotFree(channel, candidateDay)) {
          freeDayAfterNeedingConsent = candidateDay;
          break;
        }
    return {
      channel,
      before: freeDayOnOrBefore,
      after: freeDayAfterNeedingConsent,
    };
  }

  const proposedSlots = day ? channels.map((channel) => proposeSlotForChannel(channel, day)) : [];
  const someChannelNeedsLaterDay = proposedSlots.some((proposal) => !proposal.before);
  const formatDay = (demoDay: number) =>
    new Intl.DateTimeFormat(language, { weekday: 'short', day: 'numeric', month: 'short' }).format(
      new Date(DEMO_YEAR, NOVEMBER_MONTH_INDEX, demoDay)
    );
  const disconnectedChannels = channels.filter(
    (channel) =>
      accounts.find((account) => account.network === channel && isBusinessAccount(account))
        ?.health !== 'Connected'
  );
  const dateHint = !day
    ? 'Leave empty and AMY picks the next free slot.'
    : dayPickedByClient
      ? 'AMY uses the first free slot from this day.'
      : 'Taken from your text. Change it if needed.';

  function toggleChannel(channel: Channel) {
    setChannels(
      channels.includes(channel)
        ? channels.filter((selected) => selected !== channel)
        : [...channels, channel]
    );
  }

  function describeProposal(proposal: ReturnType<typeof proposeSlotForChannel>) {
    if (proposal.before)
      return (
        formatDay(proposal.before) + (proposal.before !== day ? ` (${translate('earlier')})` : '')
      );
    if (proposal.after)
      return (
        <span className="field-warn">
          {translate('next free {day}, after the date', { day: formatDay(proposal.after) })}
        </span>
      );
    return translate('no free day soon');
  }

  function send() {
    commands.addIdea(
      route.workspaceId,
      {
        text: text.trim(),
        materials: materials.map((material) => material.name),
        day,
        campaignId: campaignId || undefined,
        channels,
        allowAfter,
      },
      actor
    );
    notify(translate('Idea sent. AMY is drafting it; follow it under AMY tasks.'));
    onClose();
    navigate({ page: 'ideas', id: 'tasks' });
  }

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer add-idea"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-idea-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="add-idea-title">{translate('Add an idea')}</h2>
            <div className="muted">
              {translate('Tell AMY in your own words. Nothing is published before you approve it.')}
            </div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <label className="wizard-field">
          {translate('Your idea')}
          <textarea
            ref={fieldRef}
            className="text-field"
            rows={4}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={translate(
              'For example: Saturday coffee tasting with our new Ethiopian beans, 11–13, free for regulars.'
            )}
          />
        </label>
        <div className="chat-attachments">
          {materials.map((material) => (
            <span key={material.source + material.file} className="attachment-chip">
              {material.src ? (
                <img src={material.src} alt="" />
              ) : (
                <SafeIcon name="FiPaperclip" size={12} />
              )}
              {material.name}
              <button
                aria-label={translate('Remove {word}', { word: material.name })}
                onClick={() => setMaterials(materials.filter((other) => other !== material))}
              >
                <SafeIcon name="FiX" size={12} />
              </button>
            </span>
          ))}
        </div>
        <button className="button small" onClick={() => setPicking(true)}>
          <SafeIcon name="FiPaperclip" size={14} />
          {translate(materials.length ? 'Add more material' : 'Add a photo or video (optional)')}
        </button>
        <div className="wizard-dates">
          <label className="wizard-field">
            {translate('Suggested date')}
            <input
              type="date"
              className="text-field"
              min={isoDateFromDemoDay(DEMO_TODAY + 1)}
              max="2026-12-31"
              value={day ? isoDateFromDemoDay(day) : ''}
              onChange={(event) => {
                setDayPickedByClient(true);
                setDay(event.target.value ? demoDayFromIsoDate(event.target.value) : undefined);
              }}
            />
            <span className="muted small-print">{translate(dateHint)}</span>
          </label>
          <label className="wizard-field">
            {translate('Campaign')}
            <select
              className="select-field"
              value={campaignId}
              onChange={(event) => setCampaignId(event.target.value)}
            >
              <option value="">{translate('No campaign')}</option>
              {activeCampaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <fieldset className="wizard-channels">
          <legend>{translate('Channels')}</legend>
          <div className="filter-row">
            {networks.map((channel) => (
              <button
                key={channel}
                type="button"
                className={`channel-chip ${channels.includes(channel) ? 'selected' : ''}`}
                aria-pressed={channels.includes(channel)}
                onClick={() => toggleChannel(channel)}
              >
                <SafeIcon name={channelIconName[channel] as 'FiInstagram'} size={14} />
                {channel}
              </button>
            ))}
          </div>
          {fullChannels.length > 0 && (
            <div className="idea-dates">
              <p className="field-warn">
                {translate('{day} is already full on {channels}.', {
                  day: formatDay(day!),
                  channels: fullChannels.join(', '),
                })}
              </p>
              <ul>
                {proposedSlots.map((proposal) => (
                  <li key={proposal.channel}>
                    <strong>{proposal.channel}</strong> · {describeProposal(proposal)}
                  </li>
                ))}
              </ul>
              {someChannelNeedsLaterDay && (
                <label className="brand-check">
                  <input
                    type="checkbox"
                    checked={allowAfter}
                    onChange={(event) => setAllowAfter(event.target.checked)}
                  />{' '}
                  {translate('OK to post after the date where needed')}
                </label>
              )}
              {someChannelNeedsLaterDay && !allowAfter && (
                <p className="muted small-print">
                  {translate('Without this, AMY asks you before posting anything after the date.')}
                </p>
              )}
            </div>
          )}
          {disconnectedChannels.length > 0 && (
            <p className="field-warn">
              {translate(
                '{channels} needs reconnecting. AMY makes the other versions and tells you which one is missing.',
                { channels: disconnectedChannels.join(', ') }
              )}
            </p>
          )}
        </fieldset>
        <div className="picker-footer">
          <button className="button" onClick={onClose}>
            {translate('Cancel')}
          </button>
          <button
            className="button primary"
            disabled={readOnly || text.trim().length < MINIMUM_IDEA_LENGTH || channels.length === 0}
            onClick={send}
          >
            <SafeIcon name="FiSend" />
            {translate('Send to AMY')}
          </button>
        </div>
      </section>
      {picking && (
        <MaterialPicker
          title="Attach photos, videos or documents"
          multiple
          onCancel={() => setPicking(false)}
          onPick={(items) => {
            setMaterials([
              ...materials,
              ...items.filter((item) => !materials.some((material) => material.file === item.file)),
            ]);
            setPicking(false);
          }}
        />
      )}
    </div>
  );
}
